import { addDoc, collection, doc, getDoc, getDocs, orderBy, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, firebaseConfigured, storage } from './firebase';
import { demoAdmins, demoEvents, demoGallery, demoGreetings, demoMemories, demoNews, demoRegistrations, initialContent } from '../data/demo';
import type { AdminUser, EventItem, GalleryItem, Greeting, NewsItem, Registration, SharedMemory, SiteContent } from '../types';

const K = {
  content: 'luxframe_content_v12', events: 'luxframe_events_v12', news: 'luxframe_news_v12', gallery: 'luxframe_gallery_v12',
  greetings: 'luxframe_greetings_v12', memories: 'luxframe_memories_v12', registrations: 'luxframe_registrations_v12', admins: 'luxframe_admins_v12'
};
const readLocal = <T>(key: string, fallback: T): T => {
  const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) as T : fallback;
};
const saveLocal = <T>(key: string, value: T) => { localStorage.setItem(key, JSON.stringify(value)); return value; };
const withId = <T extends { id: string }>(arr: T[], item: T) => arr.some(x => x.id === item.id) ? arr.map(x => x.id === item.id ? item : x) : [item, ...arr];
const id = () => crypto.randomUUID();

export async function getSiteContent(): Promise<SiteContent> {
  if (!firebaseConfigured || !db) return readLocal(K.content, initialContent);
  const snap = await getDoc(doc(db, 'content', 'site'));
  return snap.exists() ? snap.data() as SiteContent : initialContent;
}
export async function saveSiteContent(content: SiteContent) {
  if (!firebaseConfigured || !db) return saveLocal(K.content, content);
  await setDoc(doc(db, 'content', 'site'), content, { merge: true }); return content;
}

export async function listEvents(): Promise<EventItem[]> {
  if (!firebaseConfigured || !db) return readLocal(K.events, demoEvents);
  const snap = await getDocs(query(collection(db, 'events'), orderBy('dateLabel', 'asc')));
  return snap.docs.map(d => ({ ...d.data(), id: d.id } as EventItem));
}

export async function listPublishedEvents(): Promise<EventItem[]> {
  if (!firebaseConfigured || !db) return readLocal(K.events, demoEvents).filter(e => e.status === 'published');
  const snap = await getDocs(query(collection(db, 'events'), where('status', '==', 'published'), orderBy('dateLabel', 'asc')));
  return snap.docs.map(d => ({ ...d.data(), id: d.id } as EventItem));
}

export async function saveEvent(event: EventItem) {
  if (!firebaseConfigured || !db) return saveLocal(K.events, withId(readLocal(K.events, demoEvents), event));
  await setDoc(doc(db, 'events', event.id), event); return event;
}

export async function listNews(): Promise<NewsItem[]> {
  if (!firebaseConfigured || !db) return readLocal(K.news, demoNews);
  const snap = await getDocs(collection(db, 'news'));
  return snap.docs.map(d => ({ ...d.data(), id: d.id } as NewsItem));
}

export async function listPublishedNews(): Promise<NewsItem[]> {
  if (!firebaseConfigured || !db) return readLocal(K.news, demoNews).filter(n => n.published);
  const snap = await getDocs(query(collection(db, 'news'), where('published', '==', true)));
  return snap.docs.map(d => ({ ...d.data(), id: d.id } as NewsItem));
}

export async function saveNews(item: NewsItem) {
  if (!firebaseConfigured || !db) return saveLocal(K.news, withId(readLocal(K.news, demoNews), item));
  await setDoc(doc(db, 'news', item.id), item); return item;
}

export async function listGallery(): Promise<GalleryItem[]> {
  if (!firebaseConfigured || !db) return readLocal(K.gallery, demoGallery);
  const snap = await getDocs(collection(db, 'gallery'));
  return snap.docs.map(d => ({ ...d.data(), id: d.id } as GalleryItem));
}

export async function listPublishedGallery(): Promise<GalleryItem[]> {
  if (!firebaseConfigured || !db) return readLocal(K.gallery, demoGallery).filter(g => g.published);
  const snap = await getDocs(query(collection(db, 'gallery'), where('published', '==', true)));
  return snap.docs.map(d => ({ ...d.data(), id: d.id } as GalleryItem));
}

export async function addGalleryImage(file: File | null, draft: Omit<GalleryItem,'id'|'image'> & { image?: string }) {
  let image = draft.image || '/assets/memoria.svg';
  if (file && firebaseConfigured && storage) {
    const folder = draft.published ? 'published' : 'pending';
    const storageRef = ref(storage, `gallery/${folder}/${Date.now()}_${file.name}`);
    await uploadBytes(storageRef, file, { contentType: file.type });
    image = await getDownloadURL(storageRef);
  } else if (file) {
    image = URL.createObjectURL(file);
  }
  const item: GalleryItem = { id: id(), image, title: draft.title, caption: draft.caption, eventId: draft.eventId, published: draft.published };
  if (!firebaseConfigured || !db) { saveLocal(K.gallery, [item, ...readLocal(K.gallery, demoGallery)]); return item; }
  await setDoc(doc(db, 'gallery', item.id), item); return item;
}

export async function listGreetings(): Promise<Greeting[]> {
  if (!firebaseConfigured || !db) return readLocal(K.greetings, demoGreetings);
  const snap = await getDocs(collection(db, 'greetings'));
  return snap.docs.map(d => ({ ...d.data(), id: d.id } as Greeting));
}

export async function listApprovedGreetings(): Promise<Greeting[]> {
  if (!firebaseConfigured || !db) return readLocal(K.greetings, demoGreetings).filter(g => g.status === 'approved');
  const snap = await getDocs(query(collection(db, 'greetings'), where('status', '==', 'approved')));
  return snap.docs.map(d => ({ ...d.data(), id: d.id } as Greeting));
}

export async function submitGreeting(input: Pick<Greeting,'name'|'message'>) {
  const greeting: Greeting = { id: id(), ...input, createdAt: new Date().toISOString(), status: 'pending_ai' };
  if (!firebaseConfigured || !db) { saveLocal(K.greetings, [greeting, ...readLocal(K.greetings, demoGreetings)]); return greeting; }
  await addDoc(collection(db, 'greetings'), greeting); return greeting;
}
export async function moderateGreeting(item: Greeting, status: Greeting['status']) {
  const updated = { ...item, status, published: status === 'approved' };
  if (!firebaseConfigured || !db) { saveLocal(K.greetings, withId(readLocal(K.greetings, demoGreetings), updated)); return; }
  await updateDoc(doc(db, 'greetings', item.id), { status, published: status === 'approved' });
}

export async function listMemories(): Promise<SharedMemory[]> {
  if (!firebaseConfigured || !db) return readLocal(K.memories, demoMemories);
  const snap = await getDocs(collection(db, 'sharedMemories'));
  return snap.docs.map(d => ({ ...d.data(), id: d.id } as SharedMemory));
}
export async function submitMemory(input: { name: string; story: string; file?: File }) {
  let image: string | undefined;
  if (input.file && firebaseConfigured && storage) {
    const storageRef = ref(storage, `submissions/pending/${Date.now()}_${input.file.name}`);
    await uploadBytes(storageRef, input.file, { contentType: input.file.type });
    image = await getDownloadURL(storageRef);
  } else if (input.file) image = URL.createObjectURL(input.file);
  const item: SharedMemory = { id:id(), name:input.name, story:input.story, image, createdAt:new Date().toISOString(), status:'pending_ai' };
  if (!firebaseConfigured || !db) { saveLocal(K.memories, [item, ...readLocal(K.memories, demoMemories)]); return item; }
  await setDoc(doc(db,'sharedMemories',item.id),item); return item;
}
export async function moderateMemory(item: SharedMemory, status: SharedMemory['status']) {
  const updated = { ...item, status };
  if (!firebaseConfigured || !db) { saveLocal(K.memories, withId(readLocal(K.memories, demoMemories), updated)); return; }
  await updateDoc(doc(db, 'sharedMemories', item.id), { status });
}

export async function listRegistrations(): Promise<Registration[]> {
  if (!firebaseConfigured || !db) return readLocal(K.registrations, demoRegistrations);
  const snap = await getDocs(collection(db, 'registrations'));
  return snap.docs.map(d => ({ ...d.data(), id:d.id } as Registration));
}
export async function registerForEvent(input: Omit<Registration,'id'|'createdAt'>) {
  const registration: Registration = { id:id(), ...input, createdAt:new Date().toISOString() };
  if (!firebaseConfigured || !db) { saveLocal(K.registrations, [registration, ...readLocal(K.registrations, demoRegistrations)]); return registration; }
  await setDoc(doc(db, 'registrations', registration.id), registration); return registration;
}
export async function listAdmins(): Promise<AdminUser[]> {
  if (!firebaseConfigured || !db) return readLocal(K.admins, demoAdmins);
  const snap = await getDocs(collection(db, 'admins'));
  return snap.docs.map(d => ({ ...d.data(), uid:d.id } as AdminUser));
}
export function drawWinner(regs: Registration[]) {
  const eligible = regs.filter(r => r.raffleOptIn && r.consent);
  if (!eligible.length) return undefined;
  return eligible[Math.floor(Math.random() * eligible.length)];
}
